import {
  css,
  CSSObject,
  InterpolationFunction,
  FlattenInterpolation,
  ThemedStyledProps,
  StyledProps,
} from 'styled-components';

type Style = InterpolationFunction<ThemedStyledProps<any, any>> | TemplateStringsArray | CSSObject;

export interface StyleDefinition<P> {
  (...styles: Style[]): (props: Partial<P>) => FlattenInterpolation<StyledProps<P>>;
  valid: (props: Partial<P>) => boolean;
}

const styledIs =
  (method: 'some' | 'every') =>
  (condition: boolean) =>
  <P, U>(...names: (keyof P | StyleDefinition<U>)[]) => {
    const isValid = (props: Partial<P & U>) =>
      names[method]((name) =>
        typeof name === 'string'
          ? name in props
            ? Boolean((props as any)[name]) === condition
            : !condition
          : (name as any).valid(props),
      );

    const getCss =
      (...styles: Style[] | any) =>
      (props: Partial<P & U>) => {
        const [style, ...rest] = styles;

        return isValid(props) ? (css(style, ...rest) as any) : (css`` as any);
      };

    getCss.valid = isValid;

    return getCss;
  };

const styledEvery = styledIs('every');
const is = styledEvery(true);
const isNot = styledEvery(false);

const styledSome = styledIs('some');
const some = styledSome(true);
const someNot = styledSome(false);

export default is;
export { is, isNot, some, someNot };
